// Client-side parsers for BibTeX, RIS, and CSV -> CiteQR reference objects.
// Returns arrays of { authors, year, title, source, volume, issue, pages, publisher, doi, url }.

const EMPTY = { authors: "", year: "", title: "", source: "", volume: "", issue: "", pages: "", publisher: "", doi: "", url: "", note: "" };

function clean(s) {
  return (s || "").replace(/[{}]/g, "").replace(/\s+/g, " ").trim();
}

function normPages(s) {
  return clean(s).replace(/--/g, "–").replace(/\s*-\s*/g, "–");
}

// ---------- BibTeX ----------
function parseBibTeX(text) {
  const refs = [];
  const entries = [];
  let i = 0;
  while (i < text.length) {
    const at = text.indexOf("@", i);
    if (at === -1) break;
    const brace = text.indexOf("{", at);
    if (brace === -1) break;
    // match balanced braces
    let depth = 0, j = brace;
    for (; j < text.length; j++) {
      if (text[j] === "{") depth++;
      else if (text[j] === "}") { depth--; if (depth === 0) { j++; break; } }
    }
    entries.push(text.slice(at, j));
    i = j;
  }
  for (const entry of entries) {
    const body = entry.slice(entry.indexOf("{") + 1);
    const fields = {};
    const re = /(\w+)\s*=\s*(\{(?:[^{}]|\{[^{}]*\})*\}|"[^"]*"|[^,}\n]+)/g;
    let m;
    while ((m = re.exec(body)) !== null) {
      const key = m[1].toLowerCase();
      let val = m[2].trim();
      if ((val.startsWith("{") && val.endsWith("}")) || (val.startsWith('"') && val.endsWith('"'))) {
        val = val.slice(1, -1);
      }
      fields[key] = val;
    }
    if (Object.keys(fields).length === 0) continue;
    const authors = clean((fields.author || "").split(/\s+and\s+/i).map((a) => clean(a)).join("; "));
    refs.push({
      ...EMPTY,
      authors,
      year: clean(fields.year || (fields.date || "").slice(0, 4)),
      title: clean(fields.title),
      source: clean(fields.journal || fields.journaltitle || fields.booktitle || ""),
      volume: clean(fields.volume || ""),
      issue: clean(fields.number || fields.issue || ""),
      pages: normPages(fields.pages || ""),
      publisher: clean(fields.publisher || ""),
      doi: clean((fields.doi || "").replace(/^https?:\/\/(dx\.)?doi\.org\//i, "")),
      url: clean(fields.url || ""),
    });
  }
  return refs;
}

// ---------- RIS ----------
function parseRIS(text) {
  const refs = [];
  let cur = null;
  const authors = [];
  const flush = () => {
    if (cur) {
      cur.authors = authors.join("; ");
      if (cur.sp || cur.ep) cur.pages = normPages([cur.sp, cur.ep].filter(Boolean).join("-"));
      delete cur.sp; delete cur.ep;
      refs.push({ ...EMPTY, ...cur });
    }
    cur = null;
    authors.length = 0;
  };
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    const m = line.match(/^([A-Z0-9]{2})\s+-\s?(.*)$/);
    if (!m) continue;
    const tag = m[1];
    const val = clean(m[2]);
    if (tag === "TY") { flush(); cur = { ...EMPTY }; continue; }
    if (!cur) cur = { ...EMPTY };
    switch (tag) {
      case "AU": case "A1": case "A2": if (val) authors.push(val); break;
      case "PY": case "Y1": cur.year = (val.match(/\d{4}/) || [""])[0]; break;
      case "TI": case "T1": cur.title = val; break;
      case "JO": case "JF": case "JA": case "T2": if (!cur.source) cur.source = val; break;
      case "VL": cur.volume = val; break;
      case "IS": cur.issue = val; break;
      case "SP": cur.sp = val; break;
      case "EP": cur.ep = val; break;
      case "DO": cur.doi = val.replace(/^https?:\/\/(dx\.)?doi\.org\//i, ""); break;
      case "UR": if (!cur.url) cur.url = val; break;
      case "PB": cur.publisher = val; break;
      case "ER": flush(); break;
      default: break;
    }
  }
  flush();
  return refs;
}

// ---------- CSV ----------
function parseCSVRows(text) {
  const rows = [];
  let row = [], field = "", inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else if (c === "\r") { /* skip */ }
      else field += c;
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((v) => v.trim() !== ""));
}

const CSV_MAP = {
  authors: "authors", author: "authors", authors_list: "authors",
  year: "year", date: "year",
  title: "title",
  source: "source", journal: "source", "journal/source": "source", publication: "source", "container-title": "source",
  volume: "volume", vol: "volume",
  issue: "issue", number: "issue", no: "issue",
  pages: "pages", page: "pages",
  publisher: "publisher",
  doi: "doi",
  url: "url", link: "url",
};

function parseCSV(text) {
  const rows = parseCSVRows(text);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const refs = [];
  for (let r = 1; r < rows.length; r++) {
    const ref = { ...EMPTY };
    headers.forEach((h, idx) => {
      const field = CSV_MAP[h];
      if (field) {
        let v = clean(rows[r][idx] || "");
        if (field === "doi") v = v.replace(/^https?:\/\/(dx\.)?doi\.org\//i, "");
        if (field === "pages") v = normPages(v);
        ref[field] = v;
      }
    });
    if (Object.values(ref).some((v) => v)) refs.push(ref);
  }
  return refs;
}

// ---------- Detect + parse ----------
export function detectFormat(text, filename = "") {
  const name = filename.toLowerCase();
  if (name.endsWith(".bib")) return "bibtex";
  if (name.endsWith(".ris")) return "ris";
  if (name.endsWith(".csv")) return "csv";
  const t = text.trimStart();
  if (/^@\w+\s*\{/.test(t)) return "bibtex";
  if (/^TY\s+-/m.test(text) || /^ER\s+-/m.test(text)) return "ris";
  if (t.includes(",") && /\n/.test(t)) return "csv";
  return "bibtex";
}

export function parseReferences(text, filename = "") {
  const format = detectFormat(text, filename);
  let refs = [];
  if (format === "bibtex") refs = parseBibTeX(text);
  else if (format === "ris") refs = parseRIS(text);
  else refs = parseCSV(text);
  return { format, refs: refs.filter((r) => r.title || r.authors || r.doi) };
}

// True only for genuine BibTeX/RIS/CSV so free-text pastes route to the review screen.
export function isStructuredImport(text, filename = "") {
  const name = (filename || "").toLowerCase();
  if (/\.(bib|ris|csv)$/.test(name)) return true;
  const t = (text || "").trimStart();
  if (/^@\w+\s*\{/.test(t)) return true;
  if (/^\s*TY\s+-/m.test(text)) return true;
  const firstLine = (text.split(/\r?\n/)[0] || "").toLowerCase();
  if (firstLine.includes(",")) {
    const known = ["authors", "author", "authors_list", "title", "year", "date", "source", "journal",
      "journal/source", "publication", "container-title", "doi", "url", "link", "volume", "vol",
      "issue", "number", "no", "pages", "page", "publisher"];
    const hits = firstLine.split(",").map((c) => c.trim()).filter((c) => known.includes(c)).length;
    if (hits >= 2) return true;
  }
  return false;
}
